<template>
    <div class="workstation-screen-editor">
        <pre v-if="isDebug">{{ backgroundImage }}</pre>
        <div class="options">
            <div class="datafields-list">
                <ui5-input
                    class="filter"
                    :value="filterDatafields"
                    @input="filterDatafields = $event.target.value"
                    placeholder="Datavelden filteren"
                />
                <ui5-list>
                    <ui5-li
                        v-for="option in visibleSpecialOptions"
                        :key="'specialButtonOption.' + option.key"
                        @click="addSpecialPlaceholder(option)"
                    >
                        {{ option.label }}
                    </ui5-li>
                    <ui5-li
                        v-for="datafield in visibleDatafields"
                        :key="datafield.datafieldId"
                        @click="onAddDatafieldClick(datafield)"
                    >
                        {{ getDatafieldLabel(datafield) }}
                    </ui5-li>
                </ui5-list>
            </div>

            <div class="sawlines-list">
                <ui5-input
                    class="filter"
                    :value="filterSawlines"
                    @input="filterSawlines = $event.target.value"
                    placeholder="Zaaglijnen filteren"
                />
                <ui5-list>
                    <ui5-li
                        v-for="sawline in visibleSawlines"
                        :key="sawline.ID"
                        @click="onAddSawlineClick(sawline)"
                    >
                        <span :style="{ fontFamily: UNICODE_FONTS }">
                            {{ SAWLINE_CHAR }}
                        </span>
                        {{ sawline.externalName }}
                    </ui5-li>
                </ui5-list>
            </div>
        </div>

        <workstation-screen-canvas
            ref="canvas"
            :width="width"
            :height="height"
            :sawlines="sawlines"
            :datafields="datafields"
            :screen-items="screenItems"
            :background-image="backgroundImage"
            :product-image="productImage"
            :handle-dragend="handleDragend"
            :handle-dragstart="handleDragstart"
        >
            <ui5-card
                ref="contextMenu"
                class="menu"
                v-if="activeItem"
                :style="{
                    top: activeItem.top + 16 + 'px',
                    left: activeItem.left + 'px',
                }"
            >
                <template
                    v-if="
                        !(
                            activeItem.type == TYPE_SPECIAL &&
                            activeItem.key == 'product.image'
                        )
                    "
                >
                    <div class="item">
                        <div class="item-content-begin">
                            <ui5-label
                                for="context-checkbox"
                                show-colon
                            >
                                Toon checkbox
                            </ui5-label>
                        </div>
                        <div class="item-content-end">
                            <ui5-switch
                                id="context-checkbox"
                                text-on="Ja"
                                text-off="Nee"
                                :checked="activeItem.showCheckbox"
                                @change="
                                    activeItem.showCheckbox =
                                        $event.target.checked
                                "
                            ></ui5-switch>
                        </div>
                    </div>

                    <div class="item">
                        <div class="item-content-begin">
                            <ui5-label
                                for="context-font-size"
                                show-colon
                            >
                                Tekst grootte
                            </ui5-label>
                        </div>
                        <div class="item-content-end">
                            <text-input
                                id="context-font-size"
                                type="number"
                                :value="activeItem.fontSize"
                                @input="activeItem.fontSize = parseInt($event)"
                            />
                        </div>
                    </div>

                    <div class="item">
                        <div class="item-content-begin">
                            <ui5-label
                                for="context-font-style"
                                show-colon
                            >
                                Tekst stijl
                            </ui5-label>
                        </div>
                        <div class="item-content-end">
                            <ui5-select
                                id="context-font-style"
                                @change="onChangeFontStyle"
                            >
                                <ui5-option
                                    value="active"
                                    :selected="
                                        activeItem.fontStyle === 'normal'
                                    "
                                >
                                    normaal
                                </ui5-option>
                                <ui5-option
                                    value="bold"
                                    :selected="activeItem.fontStyle === 'bold'"
                                >
                                    vet
                                </ui5-option>
                                <ui5-option
                                    value="italic"
                                    :selected="
                                        activeItem.fontStyle === 'italic'
                                    "
                                >
                                    cursief
                                </ui5-option>
                            </ui5-select>
                        </div>
                    </div>

                    <div class="item color-item">
                        <div class="item-content-begin">
                            <ui5-label
                                for="context-color"
                                show-colon
                            >
                                Kleur
                            </ui5-label>
                        </div>
                        <div class="item-content-end">
                            <ui5-color-picker
                                :color="activeItem.color"
                                @change="activeItem.color = $event.target.color"
                            ></ui5-color-picker>
                        </div>
                    </div>
                </template>
                <ui5-list separators="None">
                    <ui5-li @click="onDeleteActiveItemClick">Verwijder</ui5-li>
                </ui5-list>
            </ui5-card>
        </workstation-screen-canvas>
    </div>
</template>

<script>
import { getDatafieldLabel } from '../../shared/datafield';
import {
    SAWLINE_CHAR,
    specialOptions,
    TYPE_DATAFIELD,
    TYPE_SAWLINE,
    TYPE_SPECIAL,
    UNICODE_FONTS,
} from '../../shared/workstationScreens';
import ColorPickerMixin from '../../mixins/color-picker';
import IsDebugMixin from '../../mixins/is-debug';
import WorkstationScreenCanvas from './WorkstationScreenCanvas';
import TextInput from '../TextInput.vue';

export default {
    components: { WorkstationScreenCanvas, TextInput },
    mixins: [IsDebugMixin, ColorPickerMixin],
    props: {
        width: {
            type: Number,
            default: 1280,
        },
        height: {
            type: Number,
            default: 720,
        },
        datafields: {
            type: Array,
        },
        sawlines: {
            type: Array,
        },
        value: {
            type: Array,
            default: [],
        },
        backgroundImage: {
            type: Image,
            default: null,
        },
    },
    data() {
        return {
            screenItems: [],
            activeItem: null,
            filterDatafields: '',
            filterSawlines: '',
            productImage: null,
        };
    },
    computed: {
        specialOptions: () => specialOptions,
        UNICODE_FONTS: () => UNICODE_FONTS,
        SAWLINE_CHAR: () => SAWLINE_CHAR,
        TYPE_SPECIAL: () => TYPE_SPECIAL,
        visibleSpecialOptions() {
            return specialOptions.filter((x) =>
                x.label
                    .toLowerCase()
                    .includes(this.filterDatafields.toLowerCase())
            );
        },
        visibleDatafields() {
            if (!this.datafields) {
                return [];
            }
            return this.datafields.filter((x) =>
                getDatafieldLabel(x)
                    .toLowerCase()
                    .includes(this.filterDatafields.toLowerCase())
            );
        },
        visibleSawlines() {
            if (!this.sawlines) {
                return [];
            }
            return this.sawlines.filter((x) =>
                x.externalName
                    .toLowerCase()
                    .includes(this.filterSawlines.toLowerCase())
            );
        },
    },
    methods: {
        getDatafieldLabel,
        addSpecialPlaceholder(option) {
            const widthHeight = {};
            if (option.key == 'product.image') {
                widthHeight.fontSize = 264;
                widthHeight.fontStyle = !this.productImage
                    ? 198
                    : this.productImage.height *
                      (widthHeight.fontSize / this.productImage.width);
            }
            this.screenItems.push({
                id: option.key + '-' + this.screenItems.length,
                type: TYPE_SPECIAL,
                key: option.key,
                showCheckbox: false,
                fontSize: 15,
                fontStyle: 'normal',
                color: '#000000',
                top: 10,
                left: 10,
                selected: false,
                ...widthHeight,
            });
        },
        onValueChanged(value) {
            this.screenItems = value;

            if (this.$listeners && this.$listeners.input) {
                this.$emit('input', this.screenItems);
            }
        },
        handleDragstart(e) {
            this.$refs.canvas.dragItemId = e.target.id();
        },
        handleDragend(e) {
            this.$refs.canvas.dragItemId = null;
            this.onValueChanged(this.screenItems);
        },
        onAddDatafieldClick(datafield) {
            this.screenItems.push({
                id: datafield.datafieldId + '-' + this.screenItems.length,
                type: TYPE_DATAFIELD,
                datafieldId: datafield.datafieldId,
                showCheckbox: false,
                fontSize: 15,
                fontStyle: 'normal',
                color: '#000000',
                top: 10,
                left: 10,
                selected: false,
            });
        },
        onAddSawlineClick(sawline) {
            this.screenItems.push({
                id: sawline.ID + '-' + this.screenItems.length,
                type: TYPE_SAWLINE,
                sawlineId: sawline.ID,
                showCheckbox: false,
                fontSize: 15,
                fontStyle: 'normal',
                color: '#000000',
                top: 10,
                left: 10,
                selected: false,
            });
        },
        onDeleteActiveItemClick() {
            this.screenItems = this.screenItems.filter(
                (item) => item.id !== this.activeItem.id
            );
            this.activeItem = null;
            this.onValueChanged(this.screenItems);
        },
        onToggleActiveItemClick() {
            this.activeItem.showCheckbox = !this.activeItem.showCheckbox;
            this.onValueChanged(this.screenItems);
        },
        onChangeFontStyle(e) {
            this.activeItem.fontStyle = e.detail.selectedOption.value;
            this.onValueChanged(this.screenItems);
        },
        hideMenu(e) {
            // Only hide menu when clicking outside of menu
            if (
                this.$refs.contextMenu &&
                !this.$refs.contextMenu.contains(
                    e.target.ownerElement || e.target
                )
            ) {
                // Hide menu
                this.activeItem = null;
            }
        },
    },
    mounted() {
        const newImg = new Image();
        newImg.onload = () => {
            this.productImage = newImg;
        };
        newImg.src = 'images/placeholder.png';

        this.$refs.canvas
            .getStage()
            .getNode()
            .on('contextmenu', (e) => {
                e.evt.preventDefault();

                let group = e.target;
                if (group.name() != 'Group') {
                    group = group.parent;
                }

                if (group && group.name() == 'Group') {
                    const id = group.attrs.id;
                    const item = this.screenItems.find(
                        (item) => item.id.toString() === id
                    );
                    this.activeItem = item;
                }
            });

        window.addEventListener('click', this.hideMenu);
    },
    unmounted() {
        window.removeEventListener('click', this.hideMenu);
    },
    watch: {
        value: {
            handler(value) {
                this.screenItems = value;
            },
            immediate: true,
        },
    },
};
</script>
